import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IRunMethodResult } from "@multiweb/types/contracts";
import { ErrorHandler } from "UI/Errors";
import { ObjectSignatureStream } from "../../../../../Cryptography/Crypto.OW/ObjectSignatureStream";
import {
    ICryptoProvider,
    ISignatureDataStream,
    ISignedGroupInfo,
    ISignGroup,
    ISigningSession,
    ISigningStream,
} from "../../../../../Cryptography/Crypto/CryptoInterfaces";
import { IDecryptKeys } from "../../../../../Cryptography/Crypto/Messages/IDecryptKeys";
import { IKeyDef } from "../../../../../Cryptography/Crypto/Messages/MessagesInterface";
import { NativeAppStreamReader } from "../../../../../Cryptography/Crypto/NativeAppStreamReader";
import { EncryptionProviderManager } from "../../../../../Cryptography/Crypto/Providers/EncryptionProviderManager";
import { IWebCryptoProviderSettings } from "../../../../../Cryptography/Crypto/Providers/IEncryptionProviderSettings";
import { signatureManager } from "../../../../../Cryptography/Crypto/SignatureManager";
import { DataConverter } from "../../../../../Global/Base/Converters/DataConverter";
import { IEncryptedInfo, IEncryptedObjectDTO } from "../../../../../Global/Base/Crypto/IEncryptedObject";
import { ISignatureDataDTO, ISignatureDTO, ISignaturesDataDTO } from "../../../../../Global/Base/Crypto/ISignatureDataDTO";
import { ISignatureTemplate } from "../../../../../Global/Base/Crypto/ISignatureInfo";
import { ISignatureProviderProfile } from "../../../../../Global/Base/Crypto/ISignatureProvider";
import { InvalidOperationException } from "../../../../../Global/Base/Exceptions";
import { CancellationToken } from "../../../../../Global/Base/Promises/CancellationTokenSource";
import { RightType } from "../../../../../Global/Base/Rights/RightTypes";
import { SecurityUtils } from "../../../../../Global/Base/Security/SecurityUtils";
import { FileStreamReader } from "../../../../../Global/Base/Streams/FileStreamReader";
import { IFileStreamReader } from "../../../../../Global/Base/Streams/IFileStreamReader";
import { StringUtils } from "../../../../../Global/Base/Utils/StringUtils";
import { Globalization } from "../../../../../Global/Dom/Globalization";
import { IEncryptionObjectEntry, IEncryptionObjectList } from "../../../../../OW.Remoting/Remoting/DataTypes/IEncryptionObjectList";
import { OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWObjectList } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObjectList";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { PasswordManager } from "../../../../../OW.Remoting/Remoting/PasswordManager";
import { ActionContext, ActionType } from "../../../../UI.Controls/Actions/IAction";
import { ComponentRightsExtractor } from "../../../Rights/ComponentRightsExtractor";
import { ControlParams, IDigitalSignControl, IEncryptionControl } from "../../Controls/ControlInterfaces";
import { LocalizedStringBuilder } from "../../DataBuilders/LocalizedStringBuilder";
import { CryptoOperationInProgress, SignLayerBuilder } from "../../DataBuilders/SignLayerBuilder";
import { UserCertificateSelectorBuilder } from "../../DataBuilders/UserCertificateSelectorBuilder";
import { CryptoInfo } from "../../Helpers/CryptoInfo";
import { MessageButtonType, MessageType, MessageWindow } from "../../MessageWindow";
import { UIManager } from "../../UIManager";
import { IEditableFrame } from "../Frames/IEditableFrame";
import { ActionSharedMethods } from "./ActionSharedMethods";
import { MethodAction } from "./MethodAction";

/**
     * Přepravka s informace pro podepisovaný objekt
     */
class ObjectToSign implements ISigningStream {
    public readonly profile: ISignatureProviderProfile;
    public readonly template: ISignatureTemplate;
    public readonly jsonObjData: string;
    public readonly stringData: string;
    public readonly signingStream: ISignatureDataStream;
    public readonly displayStream: ISignatureDataStream;
    public readonly currentSignatureStream: ISignatureDataStream;
    public readonly obj: OWObject;
    public readonly previousSignatureData: ISignatureDataDTO;
    public readonly serializationFormat: string;

    constructor(obj: OWObject, profile: ISignatureProviderProfile, template: ISignatureTemplate, objData: object, serializationFormat: string, signatureData?: ISignatureDataDTO) {
        this.obj = obj;
        this.profile = profile;
        this.template = template;
        this.serializationFormat = serializationFormat;

        if (this.template != null) {
            this.jsonObjData = JSON.stringify(objData);
            if (signatureData) { //podepisuje se předchozí podpis
                this.previousSignatureData = signatureData;
                this.currentSignatureStream = new ObjectSignatureStream(obj.objectIdentity, this.serializationFormat, template.identifier, signatureData.signature);
                this.signingStream = new ObjectSignatureStream(obj.objectIdentity, this.serializationFormat, template.identifier, this.jsonObjData);
                this.displayStream = new ObjectSignatureStream(obj.objectIdentity, this.serializationFormat, template.identifier, this.jsonObjData);
            } else {
                this.signingStream = new ObjectSignatureStream(obj.objectIdentity, this.serializationFormat, template.identifier, this.jsonObjData);
            }
        } else { //šablona je null v případě vytváření podpisu ze stringu uloženého v _signAttr
            this.stringData = objData.toString();
            this.signingStream = new ObjectSignatureStream(obj.objectIdentity, this.serializationFormat, null, this.stringData);
        }
    }
}

const logger = LazyLoadingSectionLogger.create(LoggingSection.cryptoAction);

export class CryptoAction extends MethodAction implements IEncryptionControl, IDigitalSignControl {
    // #region Fields
    private _prepareMethod: string;
    private _signListMethodName: string = undefined;
    private _signDataAttr: string = undefined;
    private _signAttr: string = undefined;
    private _encryptCertMethodName: string = undefined; //vrací certifikát v Base64
    private _encryptListMethodName: string = undefined; //vrací certifikát v Base64
    private _decryptListMethodName: string = undefined;
    private _allowCounterSignature: boolean = false;
    private _useDigitalSign: boolean = false;
    private _useEncryption: boolean = false;
    private _useDecryption: boolean = false;
    private _userCertSelection: boolean = false;
    private _userCert: string = undefined; //uzivatelem vybrany certificat v Base64
    private _originalCertificate: string = undefined; //puvodni certifikat ziskany z _encryptCertMethodName
    private readonly _unsupportedXadesVersions = ["1.0", "1.1"];

    /**
     * Příznak, zda před vykonáním metody musí zkontrolovat doporučené atributy.
     */
    public override readonly validateRecommended: boolean;

    /**
     * Příznak, zda před vykonáním metody musí zkontrolovat odloženě povinné atributy.
     */
    public override readonly validatePostReq: boolean;
    // #endregion Fields

    // #region Properties
    public override get context(): ActionContext {
        return this.useMasterObject ? ActionContext.frameAction : ActionContext.objectAction;
    }

    public override get actionType(): ActionType {
        return ActionType.crypto;
    }

    public get useDigitalSign(): boolean {
        return this._useDigitalSign;
    }

    public set useDigitalSign(value: boolean) {
        this._useDigitalSign = value;
    }

    public get useEncryption(): boolean {
        return this._useEncryption;
    }

    public set useEncryption(value: boolean) {
        this._useEncryption = value;
    }

    public get useDecryption(): boolean {
        return this._useDecryption;
    }

    public set useDecryption(value: boolean) {
        this._useDecryption = value;
    }
    // #endregion Properties

    constructor(params: ControlParams) {
        super(params);

        this._rightsDefinition = ComponentRightsExtractor.loadRightsForTypes(this, RightType.validateIf | RightType.closeForm | RightType.useDigitalSign | RightType.useEncryption | RightType.useDecryption | RightType.confirmIf);
        this._prepareMethod = this.element.getAttribute("data-preparemethod") || undefined;
        this._signListMethodName = this.element.getAttribute("data-signlistmethodname") || undefined;
        this._signDataAttr = this.element.getAttribute("data-signdataAttr") || undefined;
        this._signAttr = this.element.getAttribute("data-signatureattr") || undefined;
        this._userCertSelection = this.getBoolDataAttr("usercertselection");
        this._allowCounterSignature = this.getBoolDataAttr("allowcountersignature", true);
        this._encryptCertMethodName = this.element.getAttribute("data-encryptCertMethodName") || undefined;
        this._encryptListMethodName = this.element.getAttribute("data-encryptListMethodName") || undefined;
        this._decryptListMethodName = this.element.getAttribute("data-decryptlistmethodname") || undefined;
        this.validateRecommended = this.getBoolDataAttr("validaterecommended");
        this.validatePostReq = this.getBoolDataAttr("validatepostreq");
    }

    // #region Methods
    protected override _validateMethodName(): void {
        // MethName není povinné
    }

    /**
     * Vyhodnotí, jestli je má být crypto actiona aktivní pro šifrování/dešifrování
     */
    //TODO-rites: přepsat, aby reflektovalo field/property _useEncryption a _useDecryption
    private async _evaluateUseEncryption(_ct?: CancellationToken): Promise<boolean> {
        const { actualObject } = this.executionContext;
        if (actualObject == null)
            return false;

        if (/*!obj.isClientSideEncrypted*/ !PasswordManager.hasPassword(actualObject.classInfo.className, actualObject.id) && !actualObject.isEncryptionFinalized && this._rightsDefinition.useEncryption) {
            this._useEncryption = false;
        }

        return this._useEncryption || this._useDecryption;
    }

    //TODO-rites: přepsat, aby reflektovalo field/property _useDigitalSign
    private async _evaluateUseDigitalSign(ct?: CancellationToken): Promise<boolean> {
        const { actualObject } = this.executionContext;
        if (actualObject != null && actualObject.isServerSideEncrypted && !PasswordManager.hasPassword(actualObject.classInfo.className, actualObject.id) //objekt je symetricky šifrovaný, ale není zadáno heslo
            || actualObject?.isEncryptionFinalized) //objekt je zašifrován asymetrickým klíčem.
            return false;

        if (!this._rightsDefinition.useDigitalSign)
            return false;

        if (!this._useDigitalSign)
            return false;
        else {
            //pokud je povolen kontrapodpis,
            if (this._allowCounterSignature !== undefined && actualObject) {
                const signatureInfo = (await OWManager.getObjectSignatureInfo(actualObject.instanceID, this._getRequestContext(ct)));
                const hasSignature = signatureInfo && signatureInfo.valid !== null;

                //1. Objekt nemá podpis, UseDigitalSign=True, AllowCounterSignature=True|False - akce je aktivní
                if (!hasSignature)
                    return this._useDigitalSign;
                //2. Objekt má podpis, UseDigitalSign=True, AllowCounterSignature=True - akce je aktivní
                else if (this._useDigitalSign && this._allowCounterSignature)
                    return true;
                //3. Objekt má podpis, UseDigitalSign=True, AllowCounterSignature=False, SignListMethod<>null - akce je aktivní, u objektů v ObjectListu, které už podpis mají by nemělo docházet k podepisování - podpis by se přepsal
                //4. Objekt má podpis, UseDigitalSign=True, AllowCounterSignature=False, SignListMethod=null - akce je neaktivní
                else if (this._useDigitalSign && !this._allowCounterSignature)
                    return this._signListMethodName != null && this._signListMethodName !== "";
            }
        }
        return this._useDigitalSign;
    }

    protected override async _internalExecute(): Promise<void> {
        const { actualObject } = this.executionContext;

        if (!(await this._checkRights(this.validateRecommended, this.validatePostReq)))
            return;

        // zkontroluje se uložení změn na owneru a jeho nadřízených framech
        if (!(await this._checkAndSaveForm())) {
            return;
        }

        // počká se na běžící updaty, aby se metoda spustila nad správnými daty
        if (actualObject != null)
            await actualObject.waitForUpdateInstance();

        if (!(await this._checkConfirmCondition()))
            return;

        await this._confirmedExecute();
    }

    /**
     * Zkontroluje, zda jsou vyplněny všechny potřebné atributy.
     */
    private _checkArguments(obj: OWObject): void {
        if (this.isStatic)
            throw new InvalidOperationException("Property IsStatic is unsupported");

        if (!obj)
            throw new InvalidOperationException("ActualObject unavailable");

        if (this._useEncryption) {
            if (!this._encryptCertMethodName && !this._userCertSelection)
                logger.error("'EncryptCertMethodName' or 'UserCertSelection' must be enabled in order to deliver a public key for asymetric encryption.");
        }
    }
    /**Vrací první crypto operaci, která bude prováděna */
    private _getFirstOperationToPerform(): CryptoOperationInProgress {
        if (this._useDigitalSign)
            return CryptoOperationInProgress.signing;
        else if (this._useEncryption)
            return CryptoOperationInProgress.encryption;
        else if (this._useDecryption)
            return CryptoOperationInProgress.decription;
        else
            return CryptoOperationInProgress.signing;
    }

    protected override async _confirmedExecute(): Promise<void> {
        try {
            const { ownerFrame, actualObject } = this.executionContext;
            const blockingLayer = new SignLayerBuilder(undefined, this._getFirstOperationToPerform());
            UIManager.instance.showBlockingLayer(blockingLayer);

            this._checkArguments(actualObject);

            let changedInstanceIds: string[] = []; //kolekce změněných objektů

            /**
             * Kryptografický proces:
             * ----------------------
             * Kroky celého procesu musí být v zásadě zachovány.
             * Celý proces (především kombinace podepsání + šifrování) se musí chovat jako transakce.
             * Důležité je to, že dokud uživatel nedokončil všechny akce procesu, tak by se neměly posílat žádné změny dat.
             * Takže nemůže proběhnout podepsání, po kterém by uživatel šifrování zrušil, ale podpisy by zůstaly zachovány.
             * Tomu je potřeba předejít nebo přinejhorším udělat rollback.
             */

            // 1) spuštění PrepareMethod
            await this._runPrepareMethod(actualObject);

            let signatures: ISignaturesDataDTO[];

            // 2) podepsání dat v nativce
            if (this._useDigitalSign) {
                /**
                 * Podepisování:
                 * -------------
                 * Požadavek na vytvoření podpisu by už neměl vracet chybové stavy (nebo na ně alespoň není potřeba reagovat).
                 * Místo toho byla zodpovědnost za celkovou validaci podpisu i certifikátu přesunuta do ukládacích metod a do OnValidate.
                 * Takže uživatel udělá podpis i případnou šifru a až ukládání může skončit validační chybou.
                 * Ta se musí zobrazit uživateli, změněný záznam se reloadne z instance na serveru, změny se projeví v GUI a frame se přepne do editace.
                 * Pokud editovat nelze, tak je to chyba návrhu formuláře.
                 * CryptoLabel by měl zobrazovat celkový nevalidní stav a uživatel z něho může získat přesné informace o tom, kde je chyba.
                 * Změny poté může zahodit standardním způsobem.
                 */

                // Pokud třída nepodporuje podepisování a jeden z atributů pro podepisovaní stringu jsou prázdné, ukonči proces a vrať chybu.
                if (!actualObject.classInfo.signingAllowed && (StringUtils.isNullOrEmpty(this._signAttr) || StringUtils.isNullOrEmpty(this._signDataAttr))) {
                    logger.error(`Cannot proceed with signing. Signing is not allowed on class '${actualObject.classInfo.className}' or string attribute.`);
                    return;
                }
                const objsToSign = await this._getObjsToSign(actualObject);
                signatures = await this._signObjects(objsToSign);
                if (!signatures || signatures.length === 0)
                    return;

                changedInstanceIds = objsToSign.map(i => i.obj.instanceID);
            }

            // 3) výběr certifikátu pro šifrování (může být zrušeno)
            // pokud se šifruje a uživatel zruší dialog pro výběr certifikátu, tak se přeruší celá operace
            if (this._useEncryption && this._userCertSelection) {
                if (!await this.showCertSelectionDialog())
                    return;
                else
                    UIManager.instance.showBlockingLayer(blockingLayer); //showCertSelectionDialog po zavření skryje i overlay vrstvu, která pak chyběla
            }

            // 4) odeslání podpisů na službu (pokud prošla uživatelská interakce)
            if (this._useDigitalSign) {
                await this._sendSignatures(actualObject, signatures);
            }

            // 5) zašifrování  (pokud prošla uživatelská interakce)
            if (this._useEncryption) {
                if (!actualObject.classInfo.encryptingAllowed) {
                    logger.error(`Cannot proceed with encryption. Encryption is not allowed on class '${actualObject.classInfo.className}'`);
                    return;
                }
                await blockingLayer.updateOperationInProgress(CryptoOperationInProgress.encryption);
                const objsToEncrypt = await this._getObjsToEncrypt(actualObject);
                await this._performEncryption(actualObject, objsToEncrypt);

                if (this._useDigitalSign) {
                    //pokud bylo zvoleno i podepisování, musí kolekce změněných objektů obsahovat jak podepisované, tak šifrované záznamy
                    objsToEncrypt.forEach(i => {
                        if (changedInstanceIds.indexOf(i.instanceID) < 0)
                            changedInstanceIds.push(i.instanceID);
                    });
                } else {
                    changedInstanceIds = objsToEncrypt.map(i => i.instanceID);
                }
            }

            // 2a) spuštění samostatného dešifrování
            const decryptParams: string[] = [];
            if (this._useDecryption) {
                await blockingLayer.updateOperationInProgress(CryptoOperationInProgress.decription);
                const decryptedData = await this._performDecryption(actualObject);

                if (!decryptedData) {
                    return;
                }

                for (const dec of decryptedData.keys) {
                    if (!dec.key) {
                        return;
                    }
                    decryptParams.push(dec.keyId);
                    decryptParams.push(dec.key);
                }
            }

            // 6) uložení objektu (objektů)
            let savingFailed = false;
            try {
                if (this.methodName) { //místo uložení AS se zavolá metoda.
                    let result: IRunMethodResult;
                    if (this._useEncryption)
                        result = await actualObject.runWithParams({
                            methodName: this.methodName,
                            params: changedInstanceIds,
                            requestContext: this._getRequestContext(),
                        });
                    else if (this._useDecryption) {
                        await blockingLayer.updateOperationInProgress(CryptoOperationInProgress.finishingDecryption);
                        result = await actualObject.runWithParams({
                            methodName: this.methodName,
                            params: decryptParams,
                            requestContext: this._getRequestContext(),
                        });
                    } else
                        result = await actualObject.runWithParams({
                            methodName: this.methodName,
                            params: changedInstanceIds,
                            requestContext: this._getRequestContext(),
                        });
                    if (result && result.messages)
                        UIManager.instance.showResultMessages(result.messages);
                } else {
                    await OWManager.applyUpdatesOnObjects(changedInstanceIds, this._getRequestContext());
                }
            } catch (e) {
                savingFailed = true;
                ActionSharedMethods.handleErrors(e, actualObject, ownerFrame);
            }

            // 7) refreshe a zaktualizování UI
            // při práci se SignListMethod je možné, že se bude pracovat s druhou instancí téhož objektu, proto je potřeba provést refresh z DB
            if (!savingFailed && ((this._useDigitalSign && this._signListMethodName) || this._useEncryption || this._useDecryption)) // u šifrování a dešifrování je potřeba načíst všechny atributy, aby se zobrazil jejich nový stav
                await actualObject.reload();
            else
                await actualObject.refresh();

            if (ownerFrame && !this.enableInEdit && !savingFailed && (this._encryptListMethodName || this._decryptListMethodName)) //v tomto případě refreshne celý seznam nebo master, protože se mohli změnit i jiné objekty než aktuálně vybraný.
                await ownerFrame.refreshData(true);

            if (actualObject.isChanged && ownerFrame instanceof IEditableFrame) // pokud je záznam stále neuložený (např. neprošel validací při ukládání), tak přepneme rodičovský frame do editace
                ownerFrame.isEditing = true;

            await this._evaluateCloseFormIfAndCloseForm();
        } catch (err) {
            ErrorHandler.instance.handleError(err);
        } finally {
            UIManager.instance.hideBlockingLayer();
        }
    }

    private async _runPrepareMethod(obj: OWObject): Promise<void> {
        if (!StringUtils.isNullOrEmpty(this._prepareMethod)) {
            const methodResult = await obj.run({
                methodName: this._prepareMethod,
                requestContext: this._getRequestContext(),
            });
            if (!methodResult)
                return;

            // aktualizace objektu prepare method mohla zmeni nejaka data na objektu
            await obj.refresh();
        }
    }

    private async _mapToTemplate(obj: OWObject, prevSignature?: ISignatureDataDTO): Promise<ObjectToSign> {
        const profile = await OWManager.getDigitalSignatureSettings(obj.instanceID, this._signDataAttr, null, this._getRequestContext());

        if (profile == null)
            throw new InvalidOperationException(`Profile for signing of record id ${obj.id} and class ${obj.objectName} unavailable.`);

        const providerType = profile.settings.providerType;
        const providerTypeUpper = providerType.toUpperCase();

        if (providerTypeUpper !== "XADES" && providerTypeUpper !== "CADES")
            throw new InvalidOperationException(`Record signing supports only XAdES or CADES signatures. Provider '${providerType}' is unsupported.`);

        if (providerTypeUpper === "XADES" && this._unsupportedXadesVersions.includes(profile.settings.providerVersion))
            throw new InvalidOperationException(`Signing provider ${providerType} version ${profile.settings.providerVersion} is deprecated and cannot be used. Use newer version.`);

        const serializationFormat = "JSON_v4"; // v tuto chvíli podporujeme pouze jeden způsob serializace
        if (!StringUtils.isNullOrEmpty(this._signDataAttr)) {
            const objData: Object = await obj.getValue(this._signDataAttr);
            return new ObjectToSign(obj, profile, null, objData, serializationFormat, null); //nevyužití šablony v případě vytváření podpisu stringu z _signDataAttr
        } else {
            const template = await OWManager.getObjectSignatureDefinition(obj.instanceID, this._getRequestContext());
            const objData = await obj.mapSignDataToTemplate(template, serializationFormat);
            return new ObjectToSign(obj, profile, template, objData, serializationFormat, prevSignature);
        }
    }

    /**
     * Vrací seznam objektů, které se mají podepsat.
     * @param obj Aktuálně vybraný objekt
     */
    private async _getObjsToSign(obj: OWObject): Promise<ObjectToSign[]> {
        const objsToSign: ObjectToSign[] = [];
        if (this._signListMethodName) {
            const methodResult = await obj.runWithParams({
                methodName: this._signListMethodName,
                params: [],
                requestContext: this._getRequestContext(),
            });
            if (methodResult && methodResult.resultValue) {
                const list = new OWObjectList(methodResult.resultValue).items;
                for (let i = 0; i < list.length; i++) {
                    const listItem = list[i].instanceID ? await OWManager.getInstance(list[i].instanceID, true) : await OWManager.getObject(list[i].id, list[i].objectName);

                    const signatureData = await OWManager.getObjectSignatureData(listItem.instanceID, this._getRequestContext());
                    objsToSign.push(await this._mapToTemplate(listItem, signatureData !== null ? signatureData : undefined));
                }
            } else
                throw new InvalidOperationException(`Result of SignListMethodName '${this._signListMethodName}' is not valid.`); //`
            if (objsToSign.length === 0)
                throw new InvalidOperationException("SignListMethodName has returned no objects to sign."); //`
        } else {
            //vrací předchozí podpis objektu nebo null
            const previousSignatureData = await OWManager.getObjectSignatureData(obj.instanceID, this._getRequestContext());
            objsToSign.push(await this._mapToTemplate(obj, previousSignatureData ? previousSignatureData : undefined));
        }
        return objsToSign;
    }

    /**
     * Vrací seznam objektů, které se mají šifrovat.
     * @param obj Aktuálně vybraný objekt
     */
    private async _getObjsToEncrypt(obj: OWObject): Promise<IEncryptionObjectEntry[]> {
        let objsToEnc: IEncryptionObjectEntry[] = [];
        if (this._encryptListMethodName) {
            const methodResult = await obj.runWithParams({
                methodName: this._encryptListMethodName,
                params: [],
                requestContext: this._getRequestContext(),
            });
            if (methodResult && methodResult.resultValue && methodResult.resultValue instanceof IEncryptionObjectList) {
                objsToEnc = methodResult.resultValue.list;
            } else
                throw new InvalidOperationException(`Result of EncryptListMethodName '${this._encryptListMethodName}' is not valid.`); //`
            if (objsToEnc.length === 0)
                logger.error("EncryptListMethodName has returned no objects to encrypt."); //`
        } else {
            const current: IEncryptionObjectEntry = { id: obj.id, objectName: obj.objectName, instanceID: obj.instanceID, encryptedKey: null, encryptedKeyBytes: null, encryptionInfo: null, key: null, keyBytes: null, objectIdentifier: null };
            objsToEnc.push(current);
        }
        return objsToEnc;
    }

    private async _getObjsToDecrypt(obj: OWObject): Promise<IEncryptionObjectEntry[]> {
        let objsToDec: IEncryptionObjectEntry[] = [];
        if (this._decryptListMethodName) {
            const methodResult = await obj.runWithParams({
                methodName: this._decryptListMethodName,
                params: [],
                requestContext: this._getRequestContext(),
            });
            if (methodResult && methodResult.resultValue && methodResult.resultValue instanceof IEncryptionObjectList) {
                objsToDec = methodResult.resultValue.list;
            } else
                throw new InvalidOperationException(`[Decryption] Result of DecryptionListMethodName '${this._decryptListMethodName}' is not valid.`); //`
            if (objsToDec.length === 0)
                throw new InvalidOperationException(`[Decryption] Result of DecryptionListMethodName '${this._decryptListMethodName}' has returned no objects.`); //`
        } else {
            const encKey = (await obj.getValue("EncKey")).valueOf();
            if (!encKey)
                throw new InvalidOperationException("[Decryption] Failed to obtain value of EncKey attribute.");

            const current: IEncryptionObjectEntry = { id: obj.id, objectName: obj.objectName, instanceID: obj.instanceID, encryptedKey: encKey, encryptedKeyBytes: null, encryptionInfo: null, key: null, keyBytes: null, objectIdentifier: obj.objectIdentity };
            objsToDec.push(current);
        }

        return objsToDec;
    }

    private async _signObjects(objs: ObjectToSign[]): Promise<ISignaturesDataDTO[]> {
        let session: ISigningSession;
        try {
            //Kolekce objektů může mít různé podpisové providery, proto se seskupí do skupin podle providera
            const groups = this._createSigningGroups(objs);

            session = await signatureManager.instance.openSession(Globalization.actualCultureISO639_1);
            const signedInfo: ISignedGroupInfo[] = await session.createTextSignatures(...groups); //odeslání do nativky

            //Nativka by měla vrátit podpisy ve stejné struktuře, jako jí byly poslány.
            return await this._createSignedDataDtOs(groups, signedInfo);
        } finally {
            if (session)
                session.dispose();
        }
    }

    private async _decryptObjects(objs: Array<{ instanceID: string; encryptedKey: string; objectidentity: string }>, provider: ICryptoProvider): Promise<any> {
        let session: ISigningSession;
        try {
            session = await signatureManager.instance.openSession(Globalization.actualCultureISO639_1);
            const cryptographicInfo = await CryptoInfo.getCryptographicInfo(objs[0].instanceID, this._getRequestContext());

            const data: IDecryptKeys = {
                provider: provider,
                keys: objs.map(item => {
                    return {
                        key: item.encryptedKey,
                        keyId: item.instanceID,
                        name: item.objectidentity,
                    } as IKeyDef;
                }),
                certificate: cryptographicInfo.encryptionInfo?.certificateBase64,
            };

            return await session.decryptObjects(data); //odeslání do nativky
        } catch (e) {
            ErrorHandler.instance.handleError(e);
            return null;
        } finally {
            if (session)
                session.dispose();
        }
    }

    /**
     * Vytvoří skupiny podepisovaných streamů podle podpisového providera.
     */
    private _createSigningGroups(objs: ObjectToSign[]): ISignGroup[] {
        const groups = new Map<string, ISignGroup>();
        for (const obj of objs) {
            const provider = obj.profile;

            if (provider == null)
                throw new InvalidOperationException("Profile for signing unavailable.");

            if (!groups.has(provider.id)) {
                groups.set(provider.id, { providerId: provider.id, provider: provider.settings, inputStreams: [] });
            }

            groups.get(provider.id).inputStreams.push(obj);
        }
        return Array.from(groups.values());
    }

    /**
     * Vytvoří DTO objekty pro hromadné uložení získaných podpisů.
     * @param groups Kolekce streamů poslaných do nativky
     * @param signedInfo Kolekce podpisů obdržené od nativky
     */
    private async _createSignedDataDtOs(groups: ISignGroup[], signedInfo: ISignedGroupInfo[]): Promise<ISignaturesDataDTO[]> {
        const ret: ISignaturesDataDTO[] = [];
        for (let i = 0; i < groups.length; i++) {
            const gObj = groups[i];
            const gSgn = signedInfo[i];

            const instanceIds = gObj.inputStreams.map(o => (o as ObjectToSign).obj.instanceID);
            const signatures = await Promise.all(gSgn.infos.map(async si => DataConverter.bufferToBase64(await new NativeAppStreamReader(si.stream).read())));
            const signedData = this._signAttr === undefined ? //pokud podepisujeme objekt/záznam, jsou data pro podpis ve formátu json, jinak jako textový řetězec (např. pro CADES BES)
                gObj.inputStreams.map(o => (o as ObjectToSign).jsonObjData) : gObj.inputStreams.map(o => (o as ObjectToSign).stringData);

            if (!(instanceIds.length === signatures.length && signatures.length === signedData.length))
                throw new Error("Number of streams to sign does not match the number of signed streams!");
            const signaturesDto: ISignatureDTO[] = instanceIds.map((o, i) => {
                return { instanceID: o, signature: signatures[i], signedData: signedData[i] };
            });
            ret.push({ signatures: signaturesDto, profileId: gObj.providerId, providerType: gObj.provider.providerType, providerVersion: gObj.provider.providerVersion });
        }
        return ret;
    }

    private async _checkEnabled(ct?: CancellationToken): Promise<boolean> {
        const useDigitalSign = await this._evaluateUseDigitalSign(ct);
        const useEncryption = await this._evaluateUseEncryption(ct);
        return useDigitalSign || useEncryption;
    }

    public async evaluateEnabledRight(actualObject: OWObject): Promise<boolean> {
        if (!this.isStatic) {
            if (actualObject) {
                return await this._checkEnabled(/*ct*/);
            } else {
                return false;
            }
        } else {
            if (!this.owner)
                return null;
            if (actualObject != null) {
                return await this._checkEnabled(/*ct*/);
            }

            const aktItemRight = await this._getItemRight(/*ct*/);
            const signRights = await this._evaluateUseDigitalSign(/*ct*/);

            return aktItemRight.Enabled && signRights;
        }
    }

    public async evaluateVisibleRight(actualObject: OWObject): Promise<boolean> {
        if (!this.isStatic) {
            if (!actualObject) {
                return false;
            }
        } else {
            if (!this.owner)
                return null;
            if (actualObject != null)
                return true;

            const aktItemRight = await this._getItemRight(/*ct*/);
            return aktItemRight.Visible;
        }

        return null;
    }

    private async _sendSignatures(obj: OWObject, signatures: ISignaturesDataDTO[]): Promise<void> {
        if (this._useEncryption) { //záznam se bude i šifrovat. Je nežádoucí aby odešla data pro DigitalSignatureSrc nešifrovaná.
            const digitalSignatureSrcInfo = await obj.getAttributeMetadata("DigitalSignatureSrc");
            if (!digitalSignatureSrcInfo || !digitalSignatureSrcInfo.encryptingAllowed)
                throw new Error("Unable to save and encrypt object signature. Atribute 'DigitalSignatureSrc' does not allows encrypted value.");

            //Projde všechyn vytvořené podpisy a zašifruje DigitalSignatureSrc
            for (const item of signatures) {
                for (const signedInfo of item.signatures) {
                    const encrypted = await SecurityUtils.encryptString(signedInfo.signedData, await this._getSaltedKey(obj));
                    signedInfo.signedData = DataConverter.bufferToBase64(encrypted);
                }
            }
        }

        if (!StringUtils.isNullOrEmpty(this._signAttr)) { //bude se podis ukládat do nějakého atributu dle _signAttr?
            for (const item of signatures) { //projde všechny vytvořené podpisy, načte instance aplikačních záznamů a nasetuje jim podpis do atributu dle _signAttr
                for (const signedInfo of item.signatures) {
                    const objItem = await OWManager.getInstance(signedInfo.instanceID, true);
                    const signAttrInfo = await objItem.getAttributeMetadata(this._signAttr);

                    if (!signAttrInfo)
                        throw new Error(`Unable to save signature to the attribute ${this._signAttr}  - does not exist.`);

                    await objItem.setValue(this._signAttr, signedInfo.signature);
                }
            }
        } else {
            // odeslání získaných podpisů na AS
            const signingResult = await OWManager.createObjectsSignature(signatures, this._useEncryption);
            if (signingResult == null)
                return; // podepisování přerušeno (chybou nebo uživatelem)

            // při podepisování více objektů v dávce nám stačí zobrazit výsledek poslední operace (teoreticky)
            const [boolResult, locKey] = OWManager.parseSigningResult(signingResult);
            if (!boolResult) {
                await MessageWindow.showAlert(new LocalizedStringBuilder(`GUI-${locKey}`)); //`
            }
        }
    }

    private async _getSaltedKey(obj: OWObject): Promise<CryptoKey> {
        if (obj.isEncryptionFinalized)
            throw new InvalidOperationException(`[Encryption] Cannot encrypt already encrypted object (${obj.classInfo.className}, ${obj.id})`); //`
        const saltedKey = await obj.getEncryptionKey();
        if (saltedKey == null)
            throw new InvalidOperationException(`[Encryption] Symetric key for object '${obj.objectIdentity}' not found.`); //`
        return saltedKey;
    }
    private async _getJwk(callingObj: OWObject): Promise<{jwk: JsonWebKey; provider: IWebCryptoProviderSettings}> {
        const encSettings = await OWManager.getEncryptionSettings(undefined, this._getRequestContext());
        const provider = EncryptionProviderManager.getEncryptionProviderByType(encSettings.algorithmName, "encrypt");
        const jwk = this._userCert ? await OWManager.getJsonWebKeyFromCertificate(this._userCert, provider.keyType, provider.algorithmName, this._getRequestContext())
            : await OWManager.getJsonWebKey(callingObj.instanceID, this._encryptCertMethodName, provider.keyType, provider.algorithmName, this._getRequestContext());
        return { jwk, provider };
    }

    private async _performEncryption(callingObj: OWObject, objsToEnc: IEncryptionObjectEntry[]): Promise<IEncryptedInfo> {
        logger.info(`[Encryption] Encrypting ${objsToEnc.length} objects: ${objsToEnc.map(o => `${o.objectName} #${o.id}`).join(", ")}`); //`

        const { jwk, provider } = await this._getJwk(callingObj);

        const encryptionList: IEncryptedObjectDTO[] = [];
        for (const encObj of objsToEnc) {
            const saltedKey = await this._getSaltedKey(await OWManager.getInstance(encObj.instanceID));
            const encryptedKeyData = await provider.encryptBuffer(await SecurityUtils.exportKey(saltedKey), jwk);
            encryptionList.push({ instanceID: encObj.instanceID, encryptedKey: DataConverter.bufferToBase64(encryptedKeyData) });
        }

        //odeslání na AS
        await OWManager.createEncryptionInfo(encryptionList, (jwk as any).x5c[0], provider.algorithmName, this._getRequestContext());
        return { encObjsList: encryptionList, certificate: (jwk as any).x5c[0], algorithmName: provider.algorithmName };
    }

    public async showCertSelectionDialog(): Promise<boolean> {
        const msg = new MessageWindow(undefined, ["dialog-encryption"]);
        //let selectCertAction: () => Promise<boolean> = null;

        const builder = new UserCertificateSelectorBuilder(msg, "GUI-641321"); // Vyberte certifikát pro šifrování

        builder.selectedFileChange.add(async (_sender, file) => {
            try {
                if (file == null) {
                    this._userCert = null;
                    builder.certificateNotSelected();
                    return;
                }

                const streamReader: IFileStreamReader = new FileStreamReader(file);
                const b64 = DataConverter.bufferToBase64(await streamReader.read());
                const certInfo = await OWManager.getCertificateInfo(b64, this._getRequestContext());
                this._userCert = b64;
                const warning = this._originalCertificate != null && this._originalCertificate !== this._userCert;
                builder.createCertificateInfo(certInfo, warning);
            } catch (e) {
                this._userCert = null;
                builder.certificateNotSelected();
                const invalidMSG = new MessageWindow();
                invalidMSG.contentBuilder = new LocalizedStringBuilder("GUI-642258"); // Chyba při kontrole vybraného certifikátu
                invalidMSG.okButtonCaption = "GUI-1"; /*OK*/
                await invalidMSG.show();
            }
        });

        msg.header = "GUI-641321"; //Vyberte certifikát pro šifrování
        msg.contentBuilder = builder;
        msg.detailButton = false;
        msg.type = MessageType.message;
        msg.okButtonCaption = "GUI-641322";
        msg.cancelButtonCaption = "GUI-33412"; //Zrušit
        msg.leftButton1Caption = "GUI-641333"; //Vybrat z počítače
        msg.leftButton2Caption = "GUI-641954"; //Vybrat jiný certifikát

        const showWindow = msg.show();

        if (this._encryptCertMethodName) {
            const obj = this.executionContext.actualObject;
            const cert = await obj.runWithParams({
                methodName: this._encryptCertMethodName,
                params: [],
                requestContext: this._getRequestContext(),
            }); //TODO - metoda musi byt bez parametru
            //let cert2 = await obj.run(this._encryptCertMethodName, undefined, undefined, this._getRequestContext()); //TODO - metoda musi byt bez parametru
            const b64 = cert.resultValue;
            if (b64) {
                this._userCert = b64;
                this._originalCertificate = b64;
                const certInfo = await OWManager.getCertificateInfo(b64, this._getRequestContext());

                builder.createCertificateInfo(certInfo);
            } else
                builder.certificateNotSelected();
        } else
            builder.certificateNotSelected();

        return (await showWindow).result === MessageButtonType.ok;
    }

    private async _performDecryption(callingObj: OWObject): Promise<any> {
        const objs = await this._getObjsToDecrypt(callingObj);
        const encryptionSettings = await OWManager.getEncryptionSettings(objs[0].instanceID, this._getRequestContext());
        const encProvider = EncryptionProviderManager.getEncryptionProviderByType(encryptionSettings.algorithmName, "decrypt");
        if (!encProvider)
            throw new InvalidOperationException("Failed to obtain encryption setting.");

        const encryptionList: Array<{ instanceID: string; encryptedKey: string; objectidentity: string }> = [];
        for (const encObj of objs) {
            if (!encObj.encryptedKey) {
                logger.error(`Object '${encObj.objectIdentifier}' cannot be decrypted. Failed to obtain its asymetric key.`); //`
                continue;
            }

            encryptionList.push({ instanceID: encObj.instanceID, encryptedKey: encObj.encryptedKey, objectidentity: encObj.objectName });
        }
        const data = await this._decryptObjects(encryptionList, { type: encProvider.algorithmName });
        return data;
    }
    // #endregion Methods
}
